import Keycloak from 'keycloak-js';
import config from '../config';

const keycloak = new Keycloak({
  url: config.KEYCLOAK_URL ?? '',
  realm: config.KEYCLOAK_REALM ?? '',
  clientId: config.KEYCLOAK_CLIENT_ID ?? '',
});

let initPromise: Promise<boolean> | null = null;

/**
 * Initialise the singleton Keycloak adapter.
 *
 * The promise is cached so that React StrictMode's double-mount (or any
 * other re-invocation) returns the same in-flight / resolved promise
 * instead of creating a second init call.
 *
 * `checkLoginIframe: false` — we don't use the Keycloak session iframe.
 * `pkceMethod: 'S256'`     — the innovate-app client requires PKCE.
 * No `onLoad` option       — passive init; only processes auth codes
 *                            already in the URL. Keycloak never
 *                            proactively redirects during init.
 *
 * sessionStorage rehydration:
 * Passive init only recognizes an OIDC auth code in the URL. When the SPA
 * does a fresh navigation (browser refresh), there's no code — so init
 * returns `authenticated: false` even though tokens are sitting in
 * sessionStorage and the user was just signed in.
 *
 * Pass the cached token+refreshToken+idToken back into init() so
 * keycloak-js treats the existing session as live. If the access token is
 * expired, the AuthProvider's refresh timer (or onTokenExpired) catches it
 * and rotates with the refresh token before the first authenticated API
 * call lands. AuthContext.applyToken() persists all three tokens on every
 * auth event, so a successful rotation immediately updates what's stored
 * here for the next reload.
 */
export const initKeycloak = (): Promise<boolean> => {
  if (!initPromise) {
    const token = sessionStorage.getItem('kc_token') ?? undefined;
    const refreshToken = sessionStorage.getItem('kc_refreshToken') ?? undefined;
    const idToken = sessionStorage.getItem('kc_idToken') ?? undefined;

    initPromise = keycloak.init({
      checkLoginIframe: false,
      pkceMethod: 'S256',
      token,
      refreshToken,
      idToken,
    });
  }
  return initPromise;
};

/**
 * Re-authentication in-flight guard (task 1037).
 *
 * Three independent paths can decide the session is dead at nearly the same
 * moment: AuthContext's proactive refresh interval, keycloak-js's
 * `onTokenExpired`, and the 401 interceptor in `services/api.ts` (a burst of
 * failing requests). Only the first may start the Keycloak login redirect;
 * a second `keycloak.login()` would build a second PKCE state and race the
 * first navigation. Module scope because the paths live in different modules
 * and this is the one module both already import.
 */
let reauthInFlight = false;

/**
 * Claim the re-authentication redirect. Returns true for the caller that should
 * start it, false when one is already in flight.
 */
export const beginReauth = (): boolean => {
  if (reauthInFlight) return false;
  reauthInFlight = true;
  return true;
};

/**
 * Release the guard. Only needed when the redirect could not start (e.g.
 * `keycloak.login()` rejected) — a successful redirect leaves the page, which
 * resets module state on its own.
 */
export const endReauth = (): void => {
  reauthInFlight = false;
};

export default keycloak;
